import {
  useGetStaffListQuery,
  useGetStaffListWithAssignCounterQuery,
} from "@/services/StaffsApi";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/UseToast";
import React, { useEffect } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { MultiSelect } from "@/components/ui/multiselect/MultiSelectCheckBox";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  useAddCounterMutation,
  useUpdateCounterMutation,
} from "@/services/CounterApi";
import { CounterDetails } from "@/app/types/counter";
import { AssigmentStatus, Status } from "@/app/shared_enums/enums";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";

const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-blue-500" },
];

interface CounterFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch?: () => void;
  setData?: React.Dispatch<React.SetStateAction<CounterDetails | undefined>>;
  data: CounterDetails | undefined;
  disableCloseConfirmation?: boolean; // Optional prop to disable confirmation dialog
}

const initialValues = {
  id: undefined,
  status: Status.Active,
  staff_ids: [] as string[],
  code:"",
};

const CounterForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
  disableCloseConfirmation = false, // Default to false
}: CounterFormProps) => {
  const { data: staffOptions, refetch: staffListRefetch } =
    useGetStaffListQuery({
      query: `sort_key=id&sort_order=asc&assignment=unassigned${action == "edit" ? `&counter_id=${data?.id}` : ""}`,
    });

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const [createCounter] = useAddCounterMutation();
  const [updateCounter] = useUpdateCounterMutation();

  const form = useForm<{
    id?: number;
    status: Status;
    staff_ids: string[];
    code:string
  }>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = form;

  useEffect(() => {
    if (action === "edit" && data) {
      const payload = { ...data };
      (payload as any).staff_ids = payload.staff_members.map(
        (staff: { id: string }) => staff.id
      );
      reset(payload);
    } else if (action === "add") {
      reset(initialValues);
      staffListRefetch()
    }
  }, [action, data, reset]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (payload: {
    id?: number;
    status: Status;
    staff_ids: string[];
    code:string;
  }) => {
    try {
      if (action === "add") {
        await createCounter({
          ...payload,
          staff_ids: payload.staff_ids,
          code:payload.code,
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Counter created successfully",
            });
            refetch?.();
            handleClose();
            staffListRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `Failed to create Counter.${err?.data?.detail || err?.data?.message}`,
            });
          });
      } else if (action === "edit") {
        await updateCounter({
          counterId: payload.id as number,
          staff_ids: payload.staff_ids,
          status: payload.status,
          code: payload.code,
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Counter updated successfully",
            });
            refetch?.();
            handleClose();
            staffListRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      }
    } catch (error: any) {
      const message =
        error?.data?.detail || error?.data?.message || "Something went wrong";
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: message,
      });
    }
  };

  const handleConfirm = () => {
    closeDialog(); // Close the dialog
    reset(); // Reset the form
    setData?.(undefined); // Clear the data
    setOpen(false); // Close the form
  };
  // const handleClose = () => {
  //   openDialog(); // Open the confirmation dialog when Cancel is clicked
  // };
  const handleClose = () => {
    clearErrors();
    reset();
    setData?.(undefined);
    setOpen(false);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (disableCloseConfirmation) {
            handleClose(); // Directly close without confirmation
          } else {
            openDialog(); // Show confirmation dialog
          }
        } else {
          setOpen(open); // Allow opening the form as usual
        }
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {action === "add" ? "Create" : "Edit"} Counter
          </SheetTitle>
          <SheetDescription>
            <Separator className="h-[1px] font-thin rounded-full" />
            <FormProvider {...form}>
              <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="flex flex-col py-4 gap-4"
                noValidate
              >
                <div className={`${action === "add" ? "hidden" : ""}`}>
                  <FloatingLabelInput
                    id="name"
                    label="Counter Id"
                    disabled
                    className="w-full"
                    {...register("id")}
                    error={errors.id?.message}
                  />
                </div>
                <div className="relative w-full">
                  <FloatingLabelInput
                    id="code"
                    label="Counter Code"
                    text="*"
                    placeholder="Add a Unique Code"
                    maxLength={40}
                    className="w-full"
                    {...register("code", {
                      required: "Required",
                      maxLength: {
                        value: 40,
                        message: "Maximum 40 characters allowed",
                      },
                    })}
                    error={errors.code?.message}
                  />
                </div>

                <div className="relative ">
                  <Controller
                    name={"staff_ids" as keyof typeof initialValues}
                    rules={{ required: "Required" }}
                    control={control}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => (
                      <MultiSelect
                        floatingLabel={"Assign staff"}
                        text="*"
                        options={
                          staffOptions as {
                            value: string;
                            label: string;
                          }[]
                        }
                        defaultValue={watch("staff_ids") || []} // Ensure defaultValue is always an array
                        onValueChange={(selectedValues) => {
                          onChange(selectedValues); // Pass selected values to state handler
                        }}
                        placeholder={"Select Staff"}
                        variant="inverted"
                        maxCount={1}
                        className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 "
                      />
                    )}
                  />

                  {errors.staff_ids?.message && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {errors.staff_ids?.message}
                    </span>
                  )}
                </div>

                <div className="relative w-full ">
                  <Controller
                    name="status"
                    rules={{ required: "Required" }}
                    control={control}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => {
                      const statusLabel = status.filter(
                        (r) => r.value == value
                      )[0];
                      return (
                        <Select
                          onValueChange={(value) => onChange(value)}
                          defaultValue={value}
                        >
                          <SelectTrigger
                            className="Capitalize w-full"
                            floatingLabel="Status"
                            text="*"
                          >
                            <SelectValue
                              placeholder="Select status"
                              className="text-gray-400"
                            >
                              <span className="flex gap-2 items-center">
                                <span
                                  className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                                ></span>
                                <span>{statusLabel?.label}</span>
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {status.map((item) => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>
                <LoadingButton
                  type="submit"
                  loading={isSubmitting}
                  className="bg-primary text-black font-semibold hover:bg-gray-200 !w-full "
                >
                  Save
                </LoadingButton>
                <ConfirmationDialog
                  isOpen={isDialogOpen} // Use the state to control visibility
                  onClose={closeDialog} // Close the dialog without taking any action
                  onConfirm={handleConfirm} // Perform the cancel action (close form)
                />
              </form>
            </FormProvider>
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};

export default CounterForm;
